import { useCallback, useState } from 'react'

import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  IssueResponse,
  IssueUpdateResponse,
} from 'features/trend-detection/types'

export interface UseIssueFeedbackResult {
  feedbackModalVisible: boolean
  feedbackIssueUuid: string | null
  feedbackIsPositive: boolean | null
  submittingFeedback: boolean
  handleFeedbackClick: (issueUuid: string, isPositive: boolean) => void
  handleFeedbackSubmit: (
    isPositive: boolean,
    additionalDetails: string,
  ) => Promise<void>
  handleFeedbackCancel: () => void
}

const NOOP_SET_ISSUES: React.Dispatch<
  React.SetStateAction<IssueResponse[]>
> = () => {}

export function useIssueFeedback(
  service: TrendDetectionService,
  projectId: string | null,
  setIssues?: React.Dispatch<React.SetStateAction<IssueResponse[]>>,
  setRunUpdates?: React.Dispatch<React.SetStateAction<IssueUpdateResponse[]>>,
): UseIssueFeedbackResult {
  const issuesSetter = setIssues ?? NOOP_SET_ISSUES
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false)
  const [feedbackIssueUuid, setFeedbackIssueUuid] = useState<string | null>(
    null,
  )
  const [feedbackIsPositive, setFeedbackIsPositive] = useState<boolean | null>(
    null,
  )
  const [submittingFeedback, setSubmittingFeedback] = useState(false)

  const handleFeedbackClick = useCallback(
    (issueUuid: string, isPositive: boolean) => {
      setFeedbackIssueUuid(issueUuid)
      setFeedbackIsPositive(isPositive)
      setFeedbackModalVisible(true)
    },
    [],
  )

  const handleFeedbackSubmit = useCallback(
    async (isPositive: boolean, additionalDetails: string): Promise<void> => {
      if (!projectId || !feedbackIssueUuid) return

      setSubmittingFeedback(true)

      try {
        await service.submitIssueFeedback(projectId, feedbackIssueUuid, {
          is_positive: isPositive,
          additional_details: additionalDetails || undefined,
        })

        issuesSetter((prevIssues) =>
          prevIssues.map((issue) =>
            issue.uuid === feedbackIssueUuid
              ? {
                  ...issue,
                  user_has_submitted_feedback: true,
                  user_feedback_is_positive: isPositive,
                }
              : issue,
          ),
        )
        if (setRunUpdates) {
          setRunUpdates((prev) =>
            prev.map((update) =>
              update.source_issue.uuid === feedbackIssueUuid
                ? {
                    ...update,
                    source_issue: {
                      ...update.source_issue,
                      user_has_submitted_feedback: true,
                      user_feedback_is_positive: isPositive,
                    },
                  }
                : update,
            ),
          )
        }

        setFeedbackModalVisible(false)
      } finally {
        setSubmittingFeedback(false)
      }
    },
    [projectId, feedbackIssueUuid, service, issuesSetter, setRunUpdates],
  )

  const handleFeedbackCancel = useCallback(() => {
    setFeedbackModalVisible(false)
    setFeedbackIssueUuid(null)
    setFeedbackIsPositive(null)
  }, [])

  return {
    feedbackModalVisible,
    feedbackIssueUuid,
    feedbackIsPositive,
    submittingFeedback,
    handleFeedbackClick,
    handleFeedbackSubmit,
    handleFeedbackCancel,
  }
}
